import { LockOutlined } from '@ant-design/icons';
import { Button, Alert, Form as AntdForm, message } from 'antd';
import { Formik, Form, useField } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useChangePasswordFromAuthenticationMutation } from '../../../api/mutations/changePasswordFromAuthentication';
import PasswordField from '../../../components/fields/PasswordField';
import LoginContent from '../../../layouts/LoginLayout/LoginContent';
import useHandleError from '../../../utilities/useHandleError';
import { type ActionHandlers } from './index';

type FormValues = { password: string; passwordRepeat: string };

const initialValues = { password: '', passwordRepeat: '' };

export type PasswordExpiredStepProps = { actions: ActionHandlers; token: string };
type PasswordExpiryStepFieldProps = {
    isSubmitting: boolean;
};

const PasswordExpiryStepField = ({ isSubmitting }: PasswordExpiryStepFieldProps) => {
    const { t } = useTranslation(['signInPage']);

    const [{ value: password }, , { setValue: setPassword }] = useField('password');
    const [{ value: passwordRepeat }, , { setValue: setPasswordRepeat }] = useField('passwordRepeat');

    useEffect(() => {
        setPassword(password.trim());
        setPasswordRepeat(passwordRepeat.trim());
    }, [password, passwordRepeat, setPassword, setPasswordRepeat]);

    return (
        <>
            <AntdForm.Item>
                <Alert description={t('signInPage:changePasswordStep.description')} type="warning" showIcon />
            </AntdForm.Item>
            <PasswordField
                name="password"
                prefix={<LockOutlined />}
                {...t('signInPage:changePasswordStep.fields.password', { returnObjects: true })}
            />
            <PasswordField
                name="passwordRepeat"
                prefix={<LockOutlined />}
                {...t('signInPage:changePasswordStep.fields.passwordRepeat', { returnObjects: true })}
            />
            <Button htmlType="submit" loading={isSubmitting} type="primary" block>
                {t('signInPage:changePasswordStep.submitBtn')}
            </Button>
        </>
    );
};

const PasswordExpiredStep = ({ actions, token }: PasswordExpiredStepProps) => {
    const { t } = useTranslation(['signInPage']);

    const [mutation] = useChangePasswordFromAuthenticationMutation();

    const onSubmit = useHandleError(
        async ({ password }: FormValues) => {
            message.loading({
                type: 'loading',
                content: t('signInPage:changePasswordStep.submittingMessage'),
                key: 'primary',
                duration: 0,
            });

            const { data } = await mutation({ variables: { token, password } }).finally(() => {
                message.destroy('primary');
            });

            switch (data.changePasswordFromAuthentication.__typename) {
                case 'AuthenticationSuccessful':
                    return actions.completeAuthentication(
                        data.changePasswordFromAuthentication.token,
                        data.changePasswordFromAuthentication.user
                    );

                default:
                    // not supported
                    return undefined;
            }
        },
        [mutation, actions, token, t]
    );

    return (
        <LoginContent title={t('signInPage:changePasswordStep.title')}>
            <Formik initialValues={initialValues} onSubmit={onSubmit}>
                {({ isSubmitting }) => (
                    <Form>
                        <PasswordExpiryStepField isSubmitting={isSubmitting} />
                    </Form>
                )}
            </Formik>
        </LoginContent>
    );
};

export default PasswordExpiredStep;
